import { useState, useCallback } from "react";
import { Check, Copy } from "lucide-react";

interface CodeBlockProps {
  code: string;
  language?: string;
}

export function CodeBlock({ code, language }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [code]);

  return (
    <div className="group relative my-3 rounded-2xl bg-white/[0.03] overflow-hidden min-w-0">
      {/* Header — barely there until hover */}
      <div className="flex items-center justify-between px-4 py-2">
        {language ? (
          <span className="text-2xs font-mono text-intri-muted/50 tracking-wide">
            {language}
          </span>
        ) : (
          <span />
        )}
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-2xs text-intri-muted/40 hover:text-intri-dim transition-colors"
          aria-label="Copy code"
        >
          {copied ? (
            <>
              <Check size={11} className="text-intri-success/70" />
              <span className="text-intri-success/70">Copied</span>
            </>
          ) : (
            <>
              <Copy size={11} />
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code */}
      <div className="overflow-x-auto">
        <pre className="px-4 pb-4 text-xs sm:text-[13px] leading-relaxed">
          <code className="font-mono text-intri-text/80">{code}</code>
        </pre>
      </div>
    </div>
  );
}
